import React, { useMemo } from 'react';
import { Calendar, Filter } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectOption,
} from '../ui';
import { SearchableSelect } from '../ui/searchable-select';
import type { TimeRecordFilters } from '../../types/time-records.types';
import type { Employee } from '../../hooks/useEmployees';
import { todayAppDateString } from '@/utils/dateFormat';

interface Department {
  id: string;
  name: string;
}

interface Organization {
  id: string;
  name: string;
}

interface Position {
  id: string;
  name: string;
}

interface PaginatedEmployees {
  data: Employee[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface PaginatedPositions {
  data: Position[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface TimeRecordsFiltersProps {
  filters: TimeRecordFilters;
  onFiltersChange: (filters: Partial<TimeRecordFilters>) => void;
  employees?: PaginatedEmployees;
  departments?: Department[];
  organizations?: Organization[];
  organizationFilter: string;
  onOrganizationChange: (orgId: string) => void;
  positions?: PaginatedPositions;
}

export function TimeRecordsFilters({
  filters,
  onFiltersChange,
  employees,
  departments,
  organizations,
  organizationFilter,
  onOrganizationChange,
  positions,
}: TimeRecordsFiltersProps) {
  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ dateFrom: e.target.value || undefined, page: 1 });
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ dateTo: e.target.value || undefined, page: 1 });
  };

  const handleEmployeeChange = (value: string | null) => {
    onFiltersChange({ employeeId: value || undefined, page: 1 });
  };

  const employeeOptions = useMemo(() => {
    return (employees?.data || []).map((emp) => ({
      value: emp.id,
      label: `${emp.last_name} ${emp.first_name}${emp.middle_name ? ' ' + emp.middle_name : ''}`,
      searchText: `${emp.last_name} ${emp.first_name} ${emp.middle_name || ''} ${emp.employee_number}`,
    }));
  }, [employees]);

  const handleOrganizationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onOrganizationChange(e.target.value);
  };

  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ department_id: e.target.value || undefined, page: 1 });
  };

  const handlePositionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ position_id: e.target.value || undefined, page: 1 });
  };

  // Get default dates (last 30 days)
  const getDefaultDateFrom = () => todayAppDateString(-30);

  const getDefaultDateTo = () => todayAppDateString();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Фильтры и поиск</CardTitle>
        </div>
        <CardDescription>
          Используйте фильтры для поиска записей учёта времени
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Date From */}
          <div className="relative">
            <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              placeholder="Дата от"
              value={filters.dateFrom || getDefaultDateFrom()}
              onChange={handleDateFromChange}
              className="pl-10"
            />
          </div>

          {/* Date To */}
          <div className="relative">
            <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              placeholder="Дата до"
              value={filters.dateTo || getDefaultDateTo()}
              onChange={handleDateToChange}
              className="pl-10"
            />
          </div>

          {/* Employee filter */}
          <SearchableSelect
            value={filters.employeeId || null}
            onChange={handleEmployeeChange}
            options={employeeOptions}
            placeholder="Сотрудник"
            searchPlaceholder="Поиск по ФИО..."
            emptyOption="Все сотрудники"
            emptySearchText="Сотрудник не найден"
          />

          {/* Organization filter */}
          <Select
            placeholder="Организация"
            value={organizationFilter}
            onChange={handleOrganizationChange}
          >
            <SelectOption value="">Все организации</SelectOption>
            {organizations?.map((org: Organization) => (
              <SelectOption key={org.id} value={org.id}>
                {org.name}
              </SelectOption>
            ))}
          </Select>

          {/* Department filter */}
          <Select
            placeholder="Подразделение"
            value={filters.department_id || ''}
            onChange={handleDepartmentChange}
          >
            <SelectOption value="">
              {organizationFilter ? 'Все подразделения' : 'Все подразделения'}
            </SelectOption>
            {departments?.map((dept: Department) => (
              <SelectOption key={dept.id} value={dept.id}>
                {dept.name}
              </SelectOption>
            ))}
          </Select>

          {/* Position filter */}
          <Select
            placeholder="Должность"
            value={filters.position_id || ''}
            onChange={handlePositionChange}
          >
            <SelectOption value="">Все должности</SelectOption>
            {positions?.data?.map((pos) => (
              <SelectOption key={pos.id} value={pos.id}>
                {pos.name}
              </SelectOption>
            ))}
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}
